import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavigation } from "@/hooks/useNavigation";
import { preloadRoute } from "@/lib/router";

export const SidebarNavItem = observer(
  ({ item, depth = 0, collapsed = false }) => {
    const { label, icon: Icon, link, subItems } = item;
    const { t } = useTran();
    const [isExpanded, setIsExpanded] = useState(false);
    const navigate = useNavigate();
    const { getItemActiveState } = useNavigation();

    const { isActive, isCurrentPage, isDescendantActive } =
      getItemActiveState(item);

    const hasLink = Boolean(link);
    const hasSubItems = Boolean(subItems && subItems.length > 0);

    // Auto-expand when active
    useEffect(() => {
      if (isActive && hasSubItems) {
        setIsExpanded(true);
      }
    }, [isActive, hasSubItems]);

    const handleClick = () => {
      if (hasSubItems) {
        setIsExpanded((previous) => !previous);
        return;
      }
      if (hasLink) {
        navigate(link.toLowerCase());
      }
    };

    // Warms a lazy route's chunk before the click, for routes that
    // registered themselves (see routePreload.js) — a no-op for any link
    // that didn't opt in.
    const handlePreload = () => {
      if (hasLink) preloadRoute(link.toLowerCase());
    };

    const activePageClasses =
      "border-l-4 border-primary bg-primary/10 text-primary ring-1 ring-primary/20";
    const activeAncestorClasses = "font-semibold text-primary hover:bg-muted";
    const hoverClasses = "text-foreground hover:bg-muted";
    const disabledClasses =
      !hasLink && !hasSubItems ? "cursor-not-allowed opacity-50" : "";
    const labelText = label ? t(label) : "";
    const navStateClasses = isCurrentPage
      ? activePageClasses
      : isDescendantActive
        ? activeAncestorClasses
        : hoverClasses;

    // Collapsed icon-only view
    if (collapsed && depth === 0) {
      const iconActivePageClass =
        "border-l-4 border-primary bg-primary/10 text-primary ring-1 ring-primary/20";
      const iconActiveAncestorClass = "text-primary";
      const iconStateClasses = isCurrentPage
        ? iconActivePageClass
        : isDescendantActive
          ? iconActiveAncestorClass
          : "text-foreground";

      return (
        <button
          type="button"
          onClick={handleClick}
          onMouseEnter={handlePreload}
          onFocus={handlePreload}
          title={t(labelText)}
          className={cn(
            "group relative flex w-full items-center justify-center rounded-radius px-2 py-2 transition-colors hover:bg-muted text-foreground",
            iconStateClasses,
          )}
        >
          {Icon && (
            <Icon
              className={cn(
                "h-5 w-5",
                isActive ? "text-primary" : "text-foreground",
              )}
            />
          )}
        </button>
      );
    }

    return (
      <div className={depth > 0 ? "ml-4" : undefined}>
        <button
          type="button"
          onClick={handleClick}
          onMouseEnter={handlePreload}
          onFocus={handlePreload}
          disabled={!hasLink && !hasSubItems}
          aria-expanded={hasSubItems ? isExpanded : undefined}
          className={cn(
            "group relative flex w-full items-center gap-3 rounded-radius px-3 py-2 text-left transition-colors",
            navStateClasses,
            disabledClasses,
          )}
        >
          {Icon && <Icon className="h-4 w-4 shrink-0" />}
          <span className="min-w-0 flex-1 break-words text-sm leading-5">
            {t(labelText)}
          </span>
          {hasSubItems && (
            <span className="flex shrink-0 items-center justify-center text-current">
              {isExpanded ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </span>
          )}
        </button>

        {hasSubItems && isExpanded && (
          <div className="mt-1 space-y-1">
            {subItems.map((child) => (
              <SidebarNavItem
                key={child.label}
                item={child}
                depth={depth + 1}
                collapsed={collapsed}
              />
            ))}
          </div>
        )}
      </div>
    );
  },
);
